/**
 * Changing a piece by saying what you want, not by typing a number.
 *
 * This project's whole argument is that you should describe music rather than
 * specify it, and then the editor turned round and asked for `density 0.54`.
 * Nobody wants 0.54. They want *busier*, and they find out whether they were
 * right by listening.
 *
 * So each verb here is one push in a direction, moving several real settings at
 * once the way a person means it. They are pure functions over a piece, which
 * is what makes them testable and what stops "brighter" quietly meaning
 * something different in two places.
 */

import type { LayerRole, Piece } from "./piece.js";
import { GROOVES } from "./grooves.js";
import { type Mode, MODE_NAMES } from "./theory.js";

export type Verb =
  | "busier" | "calmer"
  | "brighter" | "darker"
  | "faster" | "slower"
  | "moreBeat" | "lessBeat"
  | "higher" | "lower";

/**
 * The modes in order of brightness, which is a real thing musicians mean and
 * not a metaphor: each step down flattens one more degree of the scale.
 *
 * Having this as a line is what lets "brighter" be one press rather than a
 * dropdown nobody understands the contents of.
 */
const BRIGHTNESS: Mode[] = ["lydian", "major", "mixolydian", "dorian", "minor", "phrygian"];

/** Grooves from sparsest to busiest, for the beat verbs to walk. */
const BEAT_ORDER = [
  "none", "brush", "tick", "breath", "heartbeat", "pulse", "halfTime",
  "backbeat", "fourFloor", "offbeat", "marching", "skip", "pushed",
  "drivingOpen", "driving", "gallop", "drivingBusy", "scatter",
];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Step along a list from wherever the current value sits. */
function step<T>(list: T[], current: T, by: number, fallback: number): T {
  const at = list.indexOf(current);
  const from = at === -1 ? fallback : at;
  return list[clamp(from + by, 0, list.length - 1)];
}

/** Apply a change to the melodic layers only. Pad and bass have no figures. */
function eachFigureLayer(piece: Piece, change: (role: LayerRole) => void): void {
  for (const role of ["rhythm", "melody"] as const) {
    if (piece.layers[role]) change(role);
  }
}

export function nudge(input: Piece, verb: Verb): Piece {
  // Copied down to each layer, because the caller keeps the original around and
  // a verb that mutated in place would make undo impossible.
  const piece: Piece = {
    ...input,
    layers: Object.fromEntries(
      Object.entries(input.layers).map(([role, spec]) => [role, { ...spec }]),
    ) as Piece["layers"],
  };

  const [tonic, modeName = "major"] = piece.key.trim().split(/\s+/);

  switch (verb) {
    case "busier":
    case "calmer": {
      const by = verb === "busier" ? 0.14 : -0.14;
      eachFigureLayer(piece, (role) => {
        const layer = piece.layers[role]!;
        layer.density = round2(clamp((layer.density ?? 0.5) + by, 0.05, 1));
      });
      break;
    }

    case "brighter":
    case "darker": {
      const mode = step(BRIGHTNESS, modeName.toLowerCase() as Mode, verb === "brighter" ? -1 : 1, 3);
      piece.key = `${tonic} ${mode}`;
      break;
    }

    case "faster":
    case "slower":
      // Eight is enough to hear and small enough that two presses do not turn
      // an ambient piece into a different genre.
      piece.tempo = clamp(piece.tempo + (verb === "faster" ? 8 : -8), 40, 180);
      break;

    case "moreBeat":
    case "lessBeat": {
      const by = verb === "moreBeat" ? 2 : -2;
      const current = piece.layers.perc?.groove ?? "none";
      const next = step(BEAT_ORDER, current, by, 0);

      if (next === "none") {
        delete piece.layers.perc;
      } else {
        piece.layers.perc = {
          instrument: "kick",
          enterAt: 0.2,
          volume: 0.16,
          ...piece.layers.perc,
          groove: next,
          // A hand-edited pattern belongs to the groove it was edited from, so
          // moving to a different groove has to let it go.
          pattern: undefined,
        };
      }
      break;
    }

    case "higher":
    case "lower": {
      const by = verb === "higher" ? 1 : -1;
      eachFigureLayer(piece, (role) => {
        const layer = piece.layers[role]!;
        // Capped at 4 for the same reason the randomiser is: a melody builds
        // its pool two octaves above its own setting, and everything past the
        // engine's ceiling clamps to one pitch.
        layer.octave = clamp((layer.octave ?? 4) + by, 2, 4);
      });
      break;
    }
  }

  return piece;
}

/**
 * Whether a verb would actually change anything.
 *
 * A button that does nothing when pressed is worse than one that is visibly
 * unavailable, because the person assumes the tool is broken rather than that
 * they have reached the end of the range.
 */
export function canNudge(piece: Piece, verb: Verb): boolean {
  const before = JSON.stringify(piece);
  return JSON.stringify(nudge(piece, verb)) !== before;
}

/** Sanity: every groove the beat verbs walk through has to exist. */
export const BEAT_ORDER_NAMES = BEAT_ORDER;
export const KNOWN_MODES = MODE_NAMES;
export const KNOWN_GROOVES = GROOVES;
